import * as _ from "lodash";
import { BehaviorSubject } from "rxjs";

import { IScenarioPlanCalculator } from "./SimulationServices.IF";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { ProgressProvider } from "../PA.Core/Basic/Progress";
import { ASync } from "../PA.Core/Basic/ASync";
import { IMessageProvider, IProgressProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic";
import { SimulationService } from "./SimulationService";
import { DateUtils } from "../PA.Core/Basic/Dates";
import { esWB_ScnPlan } from "../PA.Core/PAdbSchema";
import { PAEnvironment } from "../PA.Core/PAEnvironment";
import { ScenarioPlanBO } from "../PA.Simulation.BusinessObjects/ScenarioPlanBO";
import { ISimulationCalculatorCalculateResults } from "../PA.Simulation.Core/PA.Simulation.IF.Aspects";

export class ScenarioPlanCalculator implements IScenarioPlanCalculator {
    private _calcMessageProvider: MessageProvider;
    private _calcProgressProvider: ProgressProvider;
    private _calcComplete: BehaviorSubject<ScenarioPlanCalculator>;
    private _scenarioPlanBO: ScenarioPlanBO;

    private _bIsLoaded: boolean;

    //   private _CalculateCompleteSubscription: Subscription
    constructor(aBO: ScenarioPlanBO) {
        this._calcMessageProvider = new MessageProvider();
        this._calcProgressProvider = new ProgressProvider();
        this._calcComplete = new BehaviorSubject<ScenarioPlanCalculator>(this);
        this._scenarioPlanBO = aBO;
        this._bIsLoaded = false;
    }

    Register(ScenarioPlanBO: ScenarioPlanBO): void {
        const self = this;
        const bo = ScenarioPlanBO;
        // Currently, we don't listen to anything,.. just on demand calc.

        this.UnRegister();
        this._scenarioPlanBO = ScenarioPlanBO;

        if (bo) {
            bo.OnLoadComplete.then((result) => {
                return ASync.resolve();
            })
                .then((ignore) => {
                    // Subscribe
                    self._bIsLoaded = true;
                })
                .catch((err) => {
                    console.log("Error in Register(): ", err);
                    self._bIsLoaded = false;
                });
        }
    }

    UnRegister(): void {
        this._scenarioPlanBO = undefined;
        this._bIsLoaded = false;
    }

    private get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    get CalcProgressProvider(): IProgressProvider {
        return this._calcProgressProvider;
    }

    get CalcMessageProvider(): IMessageProvider {
        return this._calcMessageProvider;
    }

    get OnCalculateComplete(): BehaviorSubject<ScenarioPlanCalculator> {
        return this._calcComplete;
    }

    async Calculate(opts: { bDebug?: boolean }): Promise<ISimulationCalculatorCalculateResults> {
        const self = this;
        const usrContext = PAEnvironment.getValue(PAEnvironment.UserContext);
        const aBO: ScenarioPlanBO = self._scenarioPlanBO;
        if (_.isEmpty(aBO)) {
            return;
        }

        try {
            self.CalcMessageProvider.clearMessages();
            self.CalcProgressProvider.start("Calculating Scenario");
            self.CalcProgressProvider.setProgressMessage("Saving", "Validating and Saving Scenario");
            const retVal: ISimulationCalculatorCalculateResults = {
                GUID: "", //no need to populate for the response object here
                bSuccess: true,
                results: undefined,
                sErrors: [],
                sWarnings: [],
            };

            // Save
            const aSimulationService = new SimulationService();
            aBO.WB_ScenarioPlan.WB_UserRow_CreatedBy = usrContext.User;
            await aBO.Save();

            if (!aBO.bIsSaved) {
                self.CalcProgressProvider.setProgressMessage("Saving", "Unable to Save Simulation to Calculate");
                self.CalcProgressProvider.finish();
                return;
            }

            // 2. Run Simulation Evaluator
            self.CalcProgressProvider.setProgressMessage("Calculating", "Calculating Simulation");

            const results = await aSimulationService.CalculateScenarioPlan(aBO.WB_ScenarioPlan.cif.WB_SCNPLAN_ID);

            if (results.bSuccess) {
                self.CalcMessageProvider.addMessage({
                    code: "Success",
                    type: MessageType.Information,
                    title: "Calculation Completed",
                    description: "Calculating Completed",
                });
            } else {
                self.CalcMessageProvider.addMessage({
                    code: "Failed",
                    type: MessageType.Information,
                    title: "Calculating Failed with Error",
                    description: results.results,
                });
            }
            // Add Any Warnings
            self._calcMessageProvider.addTextMessages("CalcW", MessageType.Warning, results.sWarnings);
            // Add Any Errors
            self._calcMessageProvider.addTextMessages("CalcE", MessageType.Error, results.sErrors);

            self.CalcProgressProvider.finish();

            //Update Last Calced Date
            aBO.WB_ScenarioPlan.cif.LAST_CALC_DTTS = DateUtils.convertToSAPBWTs(new Date());
            aBO.WB_ScenarioPlan.WB_UserRow_CreatedBy.cif.AUTHUSR_NAME = usrContext.User.AUTHUSR_NAME;
            await aBO.dbContext.Save(esWB_ScnPlan, aBO.WB_ScenarioPlan);

            await aBO.ReloadMetrics();
            if(aBO.WB_ScenarioPlan.bEventMode)
                await aBO.ReloadPredictors();

            self.OnCalculateComplete.next(self);

            retVal.results = results.results;
            retVal.sErrors.push(...results.sErrors);
            retVal.sWarnings.push(...results.sWarnings);
            retVal.bSuccess = retVal.sErrors.length === 0;
            return retVal;
        } catch (err) {
            self.CalcProgressProvider.exception(err);
            throw err;
        }

    }

}
